import React, {useContext, useState} from 'react';
import {Formik} from 'formik';
import * as Yup from 'yup';
import {toast} from 'react-toastify';
import {AccountRegistrationContext} from '../../contexts/account-registration.provider';
import {DownloadInviteParams} from '../../services';
import {convertStringToPhoneNumber} from '../../utils/phone-number.utils';
import {OnboardingFormComponent} from './onboarding-form.component';

interface OnboardingFormData {
  phoneNumber: string;
}

interface OnboardingFormProps {
  className?: string;
  referralCode?: string;
}

const phoneValidationSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .required('Mobile number is required')
    .test('is-valid-phone', 'Please enter a valid mobile number', function (value) {
      if (!value) return false;
      try {
        const parsed = convertStringToPhoneNumber(value);
        return !!(parsed.countryCode && parsed.phoneNumber);
      } catch {
        return false;
      }
    }),
});

const initialValues: OnboardingFormData = {
  phoneNumber: '',
};

export const OnboardingForm: React.FC<OnboardingFormProps> = ({className = '', referralCode}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {sendDownloadInvite} = useContext(AccountRegistrationContext);

  const handleSubmit = async (values: OnboardingFormData, actions: any): Promise<void> => {
    setIsSubmitting(true);
    const toastId = toast.loading('Sending download link...');

    try {
      const parsedPhone = convertStringToPhoneNumber(values.phoneNumber);

      if (!parsedPhone.countryCode || !parsedPhone.phoneNumber) {
        throw new Error('Invalid phone number format');
      }

      const params: DownloadInviteParams = {
        countryCode: parsedPhone.countryCode,
        phoneNumber: parsedPhone.phoneNumber,
        ...(referralCode && {referralCode}),
      };

      const {data, error} = await sendDownloadInvite(params);

      if (error || !data?.success) {
        toast.update(toastId, {
          render: error || 'Failed to send download link. Please try again.',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        actions.setStatus(error || 'Failed to send download link');
        return;
      }

      toast.update(toastId, {
        render: 'Download link sent successfully! Check your SMS.',
        type: 'success',
        isLoading: false,
        autoClose: 5000,
      });

      actions.resetForm();
    } catch (error: any) {
      console.error('Onboarding form submission failed:', error);
      toast.update(toastId, {
        render: error?.message || 'Failed to send download link. Please try again.',
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
      actions.setStatus(error?.message || 'Failed to send download link');
    } finally {
      setIsSubmitting(false);
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={phoneValidationSchema} onSubmit={handleSubmit}>
      {({errors, isValid, setFieldValue, touched, values}) => {
        const hasError = !!(touched.phoneNumber && errors.phoneNumber);
        const isFormValid = !!(isValid && values.phoneNumber && Object.keys(errors).length === 0);

        return (
          <OnboardingFormComponent
            className={className}
            errors={errors}
            hasError={hasError}
            isFormValid={isFormValid}
            isSubmitting={isSubmitting}
            setFieldValue={setFieldValue}
            touched={touched}
            values={values}
          />
        );
      }}
    </Formik>
  );
};
